/**
 * Session helpers shared by the `/ngos` auth gate (routes/ngos/route.tsx), the NGO data hooks
 * (data-hooks.ts) and the catch boundary (routes/-components/default-catch-boundary.tsx).
 */

/**
 * A tRPC `UNAUTHORIZED` response — the session cookie is missing or expired.
 *
 * Duck-typed rather than `instanceof TRPCClientError`: bundling can give the thrown error a
 * different class identity across module boundaries, but the transport error always carries
 * `data.code`.
 */
export function isUnauthorizedError(error: unknown): boolean {
	if (typeof error !== 'object' || error == null) return false;
	const data = (error as { data?: { code?: unknown } }).data;
	return typeof data === 'object' && data != null && data.code === 'UNAUTHORIZED';
}

const NGOS_PREFIX = '/ngos';

/**
 * The `redirect` search value for `/ngos/login`: a path relative to the NGO portal root.
 *
 * The router matches the NGO portal under an internal `/ngos` prefix while the browser URL omits it
 * (see `rewrite` in router.tsx), and both `auth.callback.ts` and `select-ngo.tsx` expect this
 * portal-relative form (e.g. `/dashboard`). Returns undefined for the portal root, which has no
 * meaningful post-login target.
 */
export function ngoLoginRedirectPath(pathname: string): string | undefined {
	const relative = pathname.startsWith(NGOS_PREFIX) ? pathname.slice(NGOS_PREFIX.length) : pathname;
	return relative && relative !== '/' ? relative : undefined;
}
